import { ReactNode, useRef, useEffect } from 'react';
import { Navigate, useLocation } from 'react-router';
import { useAuth } from '../contexts/AuthContext';
import BrandLoading from '@/shared/components/feedback/BrandLoading';

type RequiredFeature = 'training' | 'export' | 'priority_support';

interface ProtectedRouteProps {
  children: ReactNode;
  /** Optional feature that requires specific subscription plan */
  requiredFeature?: RequiredFeature;
  /** Whether this route requires an active paid subscription (default: true). Set to false for /payment/* etc. */
  requirePaidSubscription?: boolean;
  /** Where to redirect if feature not allowed (default: /pricing) */
  redirectTo?: string;
}

const ProtectedRoute = ({
  children,
  requiredFeature,
  requirePaidSubscription = true,
  redirectTo = '/pricing',
}: ProtectedRouteProps) => {
  const {
    isAuthenticated,
    currentUser,
    subscription,
    hasPaidSubscription,
    subscriptionLoading,
  } = useAuth();
  const location = useLocation();

  // FIX-8 (redirect storm): Remember once we observed a populated, paid
  // subscription this mount. If a subsequent refreshUserData() fetch
  // transiently nulls `subscription` (e.g. Supabase race or network blip
  // during the 5-min auto-refresh), we MUST NOT bounce the active session
  // to /pricing — that produces the "spontaneous redirect 2-8s after
  // arrival" symptom reported by the E2E network agent. These hooks live
  // ABOVE the early returns to satisfy the rules-of-hooks invariant.
  const previouslyPaidRef = useRef<boolean>(false);
  useEffect(() => {
    if (subscription !== null && hasPaidSubscription) {
      previouslyPaidRef.current = true;
    }
  }, [subscription, hasPaidSubscription]);

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  if (currentUser && !currentUser.email_confirmed_at) {
    return <Navigate to="/verify-email" state={{ email: currentUser.email }} replace />;
  }

  // Block on the INITIAL subscription fetch only. On periodic refreshes
  // (5-min interval, refreshUserData calls), `subscription` is already
  // populated from the previous fetch — don't flicker the gate.
  if (requirePaidSubscription && subscriptionLoading && !subscription) {
    return <BrandLoading />;
  }

  // FIX-8 (redirect storm): When subscription transiently goes null after a
  // mount already observed a paid plan, treat it as a refresh blip rather
  // than a hard "no plan" verdict. Keep the user on their current page;
  // the next fetchUserData cycle will repopulate the state.
  const isTransientNullForPaidUser =
    subscription === null && previouslyPaidRef.current;

  if (
    requirePaidSubscription &&
    !hasPaidSubscription &&
    !isTransientNullForPaidUser &&
    location.pathname !== '/pricing'
  ) {
    return <Navigate to={redirectTo} replace />;
  }

  if (requiredFeature) {
    if (subscriptionLoading && !subscription) {
      return <BrandLoading />;
    }
    // FIX-8 (redirect storm): same transient-null guard for feature gates.
    if (isTransientNullForPaidUser) {
      return <>{children}</>;
    }
    const featureAllowed = checkFeatureAccess(subscription, requiredFeature);
    if (!featureAllowed) {
      return <Navigate to={redirectTo} replace />;
    }
  }

  return <>{children}</>;
};

function checkFeatureAccess(
  subscription: { can_use_training?: boolean; can_export_data?: boolean; priority_support?: boolean } | null,
  feature: RequiredFeature
): boolean {
  if (!subscription) return false;
  switch (feature) {
    case 'training':
      return subscription.can_use_training === true;
    case 'export':
      return subscription.can_export_data === true;
    case 'priority_support':
      return subscription.priority_support === true;
  }
}

export default ProtectedRoute;
